import React, { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

import { useNetworkState } from '@uidotdev/usehooks';

import { connectionFreshness } from '../lib/connectionFreshnessBootstrap';
import { isFeatureFlagEnabled } from '../lib/meta';

import cable from '../cable';

// An open socket is not the same claim as "receiving messages", and only the second one is what
// the reader cares about. Phases that mean the client is behind get a banner; `ready` gets none.
const PHASE_LABELS = {
  offline: 'Offline',
  connecting: 'Reconnecting...',
  resuming: 'Catching up...',
  stale: 'Not up to date',
};

const PHASE_TONES = {
  offline: 'text-red-500',
  connecting: 'text-red-500',
  resuming: 'text-amber-500',
  stale: 'text-red-500',
};

const NetworkStatus = () => {
  const network = useNetworkState();

  // handle network state changes
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('output:internal:network', { detail: { network } }));
  }, [network]);

  const tracker = connectionFreshness();
  const subscribeFreshness = useCallback((onStoreChange) => tracker.subscribe(onStoreChange), [tracker]);
  const getFreshness = useCallback(() => tracker.getSnapshot(), [tracker]);
  const freshness = useSyncExternalStore(subscribeFreshness, getFreshness, getFreshness);

  // cable.state = 'idle' | 'connected' | 'restored' | 'disconnected' | 'closed'
  const [cableState, setCableState] = useState(() => cable.state);
  const [cableStateDetail, setCableStateDetail] = useState(null);

  useEffect(() => {
    const handleCable = (e) => {
      if (e.detail.connected) {
        setCableState('connected');
        setCableStateDetail(e.detail.reconnect ? 'reconnect' : 'initial');
      } else {
        setCableState('disconnected');
        setCableStateDetail(typeof e.detail.reason === 'string' ? e.detail.reason : JSON.stringify(e.detail.reason));
      }
    };

    window.addEventListener('output:internal:cable', handleCable);

    return () => {
      window.removeEventListener('output:internal:cable', handleCable);
    };
  }, [setCableState, setCableStateDetail]);

  const lagSuffix = freshness.recoveryLagSeconds === null ? '' : ` (${freshness.recoveryLagSeconds}s)`;

  // simplified view if not debug
  if (!isFeatureFlagEnabled('debug')) {
    return (
      freshness.phase !== 'ready' && (
        <span
          className={`flex p-2 text-2xs select-none ${PHASE_TONES[freshness.phase]}`}
          data-cable-state={cableState}
          data-cable-state-detail={cableStateDetail}
          data-freshness-phase={freshness.phase}
          data-freshness-lag={freshness.recoveryLagSeconds ?? undefined}
        >
          {PHASE_LABELS[freshness.phase]}
          {lagSuffix}
        </span>
      )
    );
  }

  // full debug view
  return (
    <div
      className="flex p-2 font-mono text-3xs text-gray-500 select-none [&>span:not(:last-child)]:after:px-1 [&>span:not(:last-child)]:after:content-['·']"
      data-cable-state={cableState}
      data-cable-state-detail={cableStateDetail}
      data-freshness-phase={freshness.phase}
      data-freshness-lag={freshness.recoveryLagSeconds ?? undefined}
    >
      {network.online ? <span>network online</span> : <span className="text-red-500">network offline</span>}
      {cableState === 'connected' ? <span>cable connected</span> : <span className="text-red-500">cable disconnected</span>}
      {cableStateDetail && <span>{cableStateDetail}</span>}
      {freshness.phase === 'ready' ? (
        <span>caught up</span>
      ) : (
        <span className={PHASE_TONES[freshness.phase]}>
          {freshness.phase}
          {lagSuffix}
        </span>
      )}
      {freshness.pendingRecoveries > 0 && <span>{freshness.pendingRecoveries} recovering</span>}
      {freshness.failedRecoveries > 0 && <span className="text-red-500">{freshness.failedRecoveries} failed</span>}
    </div>
  );
};

export default NetworkStatus;
